import { Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../ui/alert-dialog";

export const EditFeedbackDialog = ({ open, onOpenChange, editingFeedback, editForm, setEditForm, onSave, saving }) => (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit Training Data</DialogTitle>
          <DialogDescription>
            Update the correction or category for this feedback
          </DialogDescription>
        </DialogHeader>
        
        {editingFeedback && (
          <div className="space-y-4 py-4">
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-xs font-medium text-slate-500 mb-1">Original Question</p>
              <p className="text-sm">{editingFeedback.original_question}</p>
            </div>
            
            <div>
              <label className="text-sm font-medium text-slate-700">Correct Response</label>
              <Textarea
                value={editForm.corrected_response}
                onChange={(e) => setEditForm({...editForm, corrected_response: e.target.value})}
                className="mt-1"
                rows={4}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Category</label>
                <Select 
                  value={editForm.category} 
                  onValueChange={(v) => setEditForm({...editForm, category: v})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="payments">Payments</SelectItem>
                    <SelectItem value="invoicing">Invoicing</SelectItem>
                    <SelectItem value="onboarding">Onboarding</SelectItem>
                    <SelectItem value="account">Account Management</SelectItem>
                    <SelectItem value="compliance">Compliance</SelectItem>
                    <SelectItem value="technical">Technical Issues</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Notes</label>
                <Input
                  value={editForm.notes}
                  onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                  placeholder="Optional notes..."
                  className="mt-1"
                />
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={onSave}
            disabled={saving}
            className="gap-2 bg-violet-600 hover:bg-violet-700"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
);

export const AddFeedbackDialog = ({ open, onOpenChange, newFeedback, setNewFeedback, onSave, saving }) => (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add Training Data Manually</DialogTitle>
          <DialogDescription>
            Add a question and the correct response to train the AI
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div>
            <label className="text-sm font-medium text-slate-700">Customer Question</label>
            <Textarea
              value={newFeedback.original_question}
              onChange={(e) => setNewFeedback({...newFeedback, original_question: e.target.value})}
              placeholder="e.g., How do I process a payment?"
              className="mt-1"
              rows={2}
            />
          </div>
          
          <div>
            <label className="text-sm font-medium text-slate-700">
              AI Response (optional - leave blank if adding from scratch)
            </label>
            <Textarea
              value={newFeedback.original_ai_response}
              onChange={(e) => setNewFeedback({...newFeedback, original_ai_response: e.target.value})}
              placeholder="What the AI said (if applicable)"
              className="mt-1"
              rows={2}
            />
          </div>
          
          <div>
            <label className="text-sm font-medium text-slate-700">Correct Response *</label>
            <Textarea
              value={newFeedback.corrected_response}
              onChange={(e) => setNewFeedback({...newFeedback, corrected_response: e.target.value})}
              placeholder="The ideal response the AI should give..."
              className="mt-1"
              rows={4}
            />
          </div>
          
          <div>
            <label className="text-sm font-medium text-slate-700">Category</label>
            <Select 
              value={newFeedback.category} 
              onValueChange={(v) => setNewFeedback({...newFeedback, category: v})}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="payments">Payments</SelectItem>
                <SelectItem value="invoicing">Invoicing</SelectItem>
                <SelectItem value="onboarding">Onboarding</SelectItem>
                <SelectItem value="account">Account Management</SelectItem>
                <SelectItem value="compliance">Compliance</SelectItem>
                <SelectItem value="technical">Technical Issues</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={onSave}
            disabled={saving || !newFeedback.original_question.trim() || !newFeedback.corrected_response.trim()}
            className="gap-2 bg-violet-600 hover:bg-violet-700"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Add Training Data
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
);

export const DeleteFeedbackDialog = ({ open, onOpenChange, onConfirm }) => (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Training Data?</AlertDialogTitle>
          <AlertDialogDescription>
            This will permanently remove this feedback from the training data. 
            The AI will no longer reference this correction.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            className="bg-red-600 hover:bg-red-700"
          >
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
);

